import { useContext, useEffect } from "react";
import { SearchResultProps } from "common-client/utils/search";
import { useGetParcelsFromPointLazyQuery } from "../../generated/graphql";
import { useIsScreenSize } from "../../hooks/useWindowSize";
import { LayeredMap } from "../Maps/LayeredMap/LayeredMap";
import { Parcel, Point, Property } from "../Maps/LayeredMap/types";
import { Account, LayerContextProvider } from "../Maps/layers";
import { MIN_PARCEL_ZOOM } from "../Maps/layers/parcels";
import {
  MapViewportContext,
  MapViewportContextProvider,
} from "../Maps/utils/MapViewportContext";
import { ViewportOrBounds } from "../Maps/utils/viewportHook";
import { OldOrNewSearch } from "../Search";
import { SearchContextProvider } from "../Search/SearchContext";
import { Parcel as ParcelInput } from "./PropertyForm";

// form renders parcel number as option label in <Select />
export type ParcelOption = Pick<Parcel, "parcelNumber" | "id">;
type ParcelOptions = Array<ParcelOption>;

const MIN_VIEWPORT_ZOOM = 0;

interface PropertyFormMapProps {
  account: Account;
  marker: Maybe<Point>;
  setParcels: (parcels: ParcelOptions) => void;
  setMarker: (marker: Maybe<Point>) => void;
  handleSearchResult?: (result: SearchResultProps) => void;
  parcel: Maybe<ParcelInput>;
  setProperty: Maybe<(property?: Property) => void>;
  disabled?: boolean;
}

const InnerPropertyFormMap = ({
  parcel,
  marker,
  setParcels,
  setMarker,
  account,
  handleSearchResult,
  setProperty,
}: PropertyFormMapProps) => {
  const [getParcelsFromPointLazyQuery] = useGetParcelsFromPointLazyQuery({
    fetchPolicy: "network-only",
  });

  const { zoomTo } = useContext(MapViewportContext);

  const getParcelsFromPoint = async (point: Point): Promise<ParcelOptions> => {
    const { data } = await getParcelsFromPointLazyQuery({
      variables: { ...point },
    });

    return (
      data?.parcels.filter<ParcelOption>((parcel): parcel is ParcelOption => {
        return !!parcel.parcelNumber;
      }) ?? []
    );
  };

  const layers = {
    marker: {
      value: marker,
    },
    parcels: {
      value: { id: parcel?.id },
      interactive: {
        click: true,
        hover: true,
      },
      onClick: (parcels: Array<Parcel>) => {
        setProperty?.();
        setParcels(parcels);

        const parcel = parcels[0];
        if (parcel) {
          setMarker(parcel.point);
        }
      },
    },
    accountBoundary: {
      interactive: {
        click: true,
      },
      onClick: async (point: Point) => {
        const pointParcels = await getParcelsFromPoint(point);
        setParcels(pointParcels);
        setMarker(point);
        setProperty?.();
      },
    },
    properties: {
      interactive: {
        hover: true,
        click: !!setProperty,
      },
      ...(setProperty && {
        onClick: async (property: Property) => {
          const pointParcels = await getParcelsFromPoint(property.point);
          setParcels(pointParcels);
          setMarker(property.point);
          setProperty(property);
        },
      }),
    },
  };

  useEffect(() => {
    if (layers.marker.value) {
      zoomTo({ ...layers.marker.value, zoom: MIN_PARCEL_ZOOM });
    }
  }, [layers.marker.value, zoomTo]);

  const isSmallScreen = useIsScreenSize("mobile", "tablet");

  return (
    <LayerContextProvider
      account={account}
      // we want the property dots to always be visible when creating
      // a property so we know what *other* properties are available
      defaultState={{ buildings: { properties: true } }}
      noLayerPanel={true}
    >
      <SearchContextProvider
        handleSearchResultClick={handleSearchResult ?? (() => {})}
      >
        <LayeredMap
          width={"100%"}
          height={"100%"}
          baseMapStyle={account.defaultBaseMap.mapboxStyle}
          account={account}
          layers={layers}
          minZoom={MIN_VIEWPORT_ZOOM}
          halfMap={true}
          topLeftMapOverlayItems={
            handleSearchResult ?
              [
                <OldOrNewSearch
                  compactSearchPanel={isSmallScreen}
                  overlaySearchPanel={isSmallScreen}
                  addressOnly
                />,
              ]
            : undefined
          }
        />
      </SearchContextProvider>
    </LayerContextProvider>
  );
};

export const PropertyFormMap = ({
  initialViewport,
  ...props
}: PropertyFormMapProps & { initialViewport: ViewportOrBounds }) => {
  return (
    <MapViewportContextProvider initialViewport={initialViewport}>
      <InnerPropertyFormMap {...props} />
    </MapViewportContextProvider>
  );
};
